import type { V2mom, V2momEntry } from '@/lib/v2mom/v2momModel';
import { buildV2momTree, V2MOM_STATUS_LABEL } from '@/lib/v2mom/v2momModel';

export interface V2momPrintBodyProps {
  v2mom: V2mom;
  entries: V2momEntry[];
  parentTitle: string | null;
  orgUnitName: string | null;
}

const H2 =
  'text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-sec)] border-b border-[var(--rule)] pb-1';

function Line({ entry, numbered }: { entry: V2momEntry; numbered?: string }) {
  return (
    <li className="text-[var(--text-pri)] print:text-black">
      {numbered && <span className="mr-1.5 font-mono text-[11px] text-[var(--text-ter)]">{numbered}</span>}
      <span className="font-medium">{entry.title}</span>
      {entry.target && <span className="text-[var(--text-sec)]"> — {entry.target}</span>}
      {entry.detail?.trim() && <div className="whitespace-pre-wrap text-[var(--text-sec)]">{entry.detail}</div>}
    </li>
  );
}

export function V2momPrintBody({ v2mom, entries, parentTitle, orgUnitName }: V2momPrintBodyProps) {
  const tree = buildV2momTree(entries);
  const title = v2mom.title?.trim() || 'Untitled V2MOM';
  const meta = [
    V2MOM_STATUS_LABEL[v2mom.status],
    v2mom.periodLabel,
    orgUnitName,
    parentTitle ? `Aligns to ${parentTitle}` : null,
  ].filter(Boolean);

  return (
    <div className="space-y-8 text-[13px] leading-relaxed">
      <div className="print-field-block space-y-1 border-b border-[var(--rule)] pb-5">
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-pri)] print:text-black">{title}</h1>
        <p className="text-[11px] uppercase tracking-wider text-[var(--text-ter)]">
          V2MOM · {meta.join(' · ')} · Priorities.ai print view · {new Date().toLocaleString()}
        </p>
      </div>

      <section className="print-field-block space-y-1.5">
        <h2 className={H2}>Vision</h2>
        <div className="whitespace-pre-wrap text-[var(--text-pri)] print:text-black">
          {v2mom.vision?.trim() ? v2mom.vision.trim() : '—'}
        </div>
      </section>

      <section className="print-field-block space-y-2">
        <h2 className={H2}>Values</h2>
        {tree.values.length ? (
          <ul className="list-disc space-y-1 pl-5">
            {tree.values.map((e) => <Line key={e.id} entry={e} />)}
          </ul>
        ) : (
          <p className="text-[var(--text-sec)]">None yet.</p>
        )}
      </section>

      <section className="print-field-block space-y-3">
        <h2 className={H2}>Methods</h2>
        {tree.methods.length ? (
          <ol className="space-y-4">
            {tree.methods.map((g, i) => (
              <li key={g.method.id} className="space-y-1.5">
                <div className="text-[var(--text-pri)] print:text-black">
                  <span className="mr-1.5 font-mono text-[11px] text-[var(--text-ter)]">{i + 1}.</span>
                  <span className="font-medium">{g.method.title}</span>
                  {g.method.detail?.trim() && (
                    <div className="whitespace-pre-wrap text-[var(--text-sec)]">{g.method.detail}</div>
                  )}
                </div>
                {(g.obstacles.length > 0 || g.measures.length > 0) && (
                  <div className="grid grid-cols-1 gap-3 pl-5 md:grid-cols-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-[var(--text-ter)]">Obstacles</p>
                      {g.obstacles.length ? (
                        <ul className="list-disc space-y-0.5 pl-4">
                          {g.obstacles.map((e) => <Line key={e.id} entry={e} />)}
                        </ul>
                      ) : (
                        <p className="text-[var(--text-sec)]">—</p>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide text-[var(--text-ter)]">Measures</p>
                      {g.measures.length ? (
                        <ul className="list-disc space-y-0.5 pl-4">
                          {g.measures.map((e) => <Line key={e.id} entry={e} />)}
                        </ul>
                      ) : (
                        <p className="text-[var(--coral)]">No measure yet</p>
                      )}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-[var(--text-sec)]">None yet.</p>
        )}
      </section>

      {(tree.obstacles.length > 0 || tree.measures.length > 0) && (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
          <section className="print-field-block space-y-2">
            <h2 className={H2}>Obstacles across the whole plan</h2>
            {tree.obstacles.length ? (
              <ul className="list-disc space-y-1 pl-5">{tree.obstacles.map((e) => <Line key={e.id} entry={e} />)}</ul>
            ) : (
              <p className="text-[var(--text-sec)]">—</p>
            )}
          </section>
          <section className="print-field-block space-y-2">
            <h2 className={H2}>Measures across the whole plan</h2>
            {tree.measures.length ? (
              <ul className="list-disc space-y-1 pl-5">{tree.measures.map((e) => <Line key={e.id} entry={e} />)}</ul>
            ) : (
              <p className="text-[var(--text-sec)]">—</p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
